<template>
    <!-- Clickable only when there is something to open for this reader. -->
    <div class="noti-row" :class="{ unread: !n.read, clickable: !!target }" :role="target ? 'button' : undefined"
        :tabindex="target ? 0 : undefined" @click="open" @keydown.enter="open">
        <v-icon :color="color" size="22" class="noti-level">{{ icon }}</v-icon>
        <div class="noti-main">
            <div class="noti-title" :class="{ 'font-weight-medium': !n.read }">{{ title }}</div>
            <div v-if="facts" class="noti-facts text-medium-emphasis">{{ facts }}</div>
            <template v-if="detail">
                <button class="noti-more" type="button" @click.stop="expanded = !expanded">
                    {{ expanded ? t("hide_details") : t("show_details") }}
                    <v-icon size="16">{{ expanded ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
                </button>
                <pre v-if="expanded" class="noti-detail" :class="prose ? 'noti-detail--prose' : 'pit-mono'"
                    @click.stop>{{ detail }}</pre>
            </template>
        </div>
        <span class="noti-time pit-mono text-medium-emphasis">{{ time }}</span>
    </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { usePitStoreBasics } from '@/stores/app_common'
import { useNotificationsStore } from '@/stores/notifications'
import { useReportsStore } from '@/stores/reports'
import { useSessionStore } from '@/stores/session'
import {
    LEVEL_COLOR, LEVEL_ICON, detailIsProse, notificationDetail, notificationFacts, notificationTarget, notificationTime,
    notificationTitle,
} from '@/utils/notifications'

const props = defineProps({ n: { type: Object, required: true } })
const emit = defineEmits(['opened'])

const { t } = usePitStoreBasics()
const router = useRouter()
const store = useNotificationsStore()
const reports = useReportsStore()
const session = useSessionStore()

const expanded = ref(false)

const title = computed(() => notificationTitle(props.n, t))
const facts = computed(() => notificationFacts(props.n, title.value))
const detail = computed(() => notificationDetail(props.n, title.value))
const prose = computed(() => detailIsProse(props.n))
const time = computed(() => notificationTime(props.n.created, t))
const icon = computed(() => LEVEL_ICON[props.n.level] || LEVEL_ICON.info)
const color = computed(() => LEVEL_COLOR[props.n.level] || LEVEL_COLOR.info)
const target = computed(() => notificationTarget(props.n, {
    isProdWorker: session.iam_prod_worker,
    isManagementDoc: (type) => reports.isManagementType(type),
}))

// Opening a row marks it read and follows its target. A row with nothing to
// open is not clickable, so it counts as read once it has been shown.
const open = () => {
    const tg = target.value
    if (!tg) return
    store.markRead(props.n)
    if (tg.url) window.open(tg.url, '_blank', 'noopener')
    else router.push(tg.route).catch(() => { })
    emit('opened')
}
onMounted(() => {
    if (!target.value) store.markRead(props.n)
})
</script>

<style scoped>
.noti-row {
    position: relative;
    display: flex;
    gap: 12px;
    align-items: flex-start;
    padding: 12px 14px 12px 18px;
    border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.noti-row.clickable {
    cursor: pointer;
}

.noti-row.unread {
    background: rgba(var(--v-theme-accent), 0.06);
}

.noti-row.unread::before {
    content: "";
    position: absolute;
    left: 6px;
    top: 20px;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: rgb(var(--v-theme-accent));
}

.noti-level {
    margin-top: 1px;
    flex: none;
}

.noti-main {
    flex: 1;
    min-width: 0;
}

.noti-title {
    font-size: 14px;
    line-height: 1.35;
    overflow-wrap: anywhere;
}

.noti-facts {
    font-size: 12.5px;
    margin-top: 2px;
    overflow-wrap: anywhere;
}

.noti-more {
    margin-top: 4px;
    display: inline-flex;
    align-items: center;
    gap: 2px;
    font-size: 12.5px;
    font-weight: 500;
    color: rgb(var(--v-theme-accent));
    background: none;
    border: 0;
    padding: 0;
    cursor: pointer;
}

.noti-detail {
    margin: 6px 0 0;
    padding: 6px 8px;
    max-height: 22em;
    overflow: auto;
    border-radius: 4px;
    background: rgba(var(--v-theme-on-surface), 0.05);
    font-size: 11.5px;
    line-height: 1.45;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    cursor: text;
}

.noti-detail--prose {
    padding: 0;
    background: none;
    font-family: inherit;
    font-size: 12.5px;
}

.noti-time {
    flex: none;
    font-size: 12px;
    margin-top: 2px;
}
</style>
